import {
  PayloadAction,
  createAction,
  createSlice,
} from '@reduxjs/toolkit'
import { eventChannel } from 'redux-saga'
import {
  all,
  call,
  cancelled,
  put,
  take,
  takeEvery,
} from 'redux-saga/effects'

import * as schema from '../schema'
import { FireEvent, getDatabase } from '../api'
import { DeepReadonly, log, processErr } from '../utils'
import { YieldReturn } from './common'

const PREFIX = 'setters'

type SettersState = DeepReadonly<{
  setters: schema.CloserOrSetter[]
}>

const initialState: SettersState = {
  setters: [],
}

type SettersChannelPayload = FireEvent<schema.CloserOrSetter>[]

const settersSlice = createSlice({
  name: PREFIX,
  initialState,
  reducers: {
    settersAction() {
      // TODO: ??
      // state.setters
    },
    receivedSetters(
      state,
      {
        payload: { setters },
      }: PayloadAction<{ setters: schema.CloserOrSetter[] }>,
    ) {
      state.setters = setters
    },
  },
})

export const {
  actions: { settersAction, receivedSetters },
  reducer: setters,
} = settersSlice

export const addSetter = createAction<schema.CloserOrSetter>(
  `${PREFIX}/addSetter`,
)
export const removeSetter = createAction<schema.CloserOrSetter>(
  `${PREFIX}/removeSetter`,
)
export const updateSetter = createAction<{
  old: schema.CloserOrSetter
  updated: schema.CloserOrSetter
}>(`${PREFIX}/updateSetter`)

export type SetterOpResult = { readonly ok: boolean; readonly err?: string }
export const setterOpResult = createAction<SetterOpResult>(
  `${PREFIX}/setterOpResult`,
)

//#region selectors

interface GlobalState {
  [PREFIX]: SettersState
}

export type SelectSetters = (state: GlobalState) => schema.CloserOrSetter[]

export const selectSetters =
  () =>
  ({ setters: { setters } }: GlobalState): schema.CloserOrSetter[] =>
    setters as schema.CloserOrSetter[]

//#endregion selectors

const createSettersChannel = () =>
  eventChannel<SettersChannelPayload | Error>((emit) => {
    const dbRef = getDatabase().ref(`Setters`)

    dbRef.on('value', (data) => {
      emit(data.val() as SettersChannelPayload)
    })

    return () => {
      dbRef.off()
    }
  })

function* settersWatcher() {
  const settersChannel: YieldReturn<typeof createSettersChannel> = yield call(
    createSettersChannel,
  )
  try {
    while (true) {
      const payload: SettersChannelPayload = yield take(settersChannel)
      yield put(
        receivedSetters({
          setters: payload as unknown as schema.CloserOrSetter[],
        }),
      )
    }
  } finally {
    log(`setters sub finally()`)

    if ((yield cancelled()) as boolean) {
      log('setters sub finally() -> cancelled() , will close channel.')
      settersChannel.close()
    }
  }
}

function* addSetterFn({ payload }: ReturnType<typeof addSetter>) {
  try {
    const db = getDatabase()
    const snap: { val(): schema.CloserOrSetter[] | null } = yield call(() =>
      db.ref('Setters').once('value'),
    )
    const current = (snap.val() || []) as schema.CloserOrSetter[]
    yield call(() => db.ref('Setters').set([...current, payload]))
    yield put(setterOpResult({ ok: true }))
  } catch (e) {
    log(`${addSetterFn.name}*() -> ${processErr(e)}`)
    yield put(setterOpResult({ ok: false, err: processErr(e) }))
  }
}

function* removeSetterFn({ payload }: ReturnType<typeof removeSetter>) {
  try {
    const db = getDatabase()
    const snap: { val(): schema.CloserOrSetter[] | null } = yield call(() =>
      db.ref('Setters').once('value'),
    )
    const current = (snap.val() || []) as schema.CloserOrSetter[]
    yield call(() =>
      db.ref('Setters').set(current.filter((s) => s.value !== payload.value)),
    )
    yield put(setterOpResult({ ok: true }))
  } catch (e) {
    log(`${removeSetterFn.name}*() -> ${processErr(e)}`)
    yield put(setterOpResult({ ok: false, err: processErr(e) }))
  }
}

function* updateSetterFn({
  payload: { old, updated },
}: ReturnType<typeof updateSetter>) {
  try {
    const db = getDatabase()
    const snap: { val(): schema.CloserOrSetter[] | null } = yield call(() =>
      db.ref('Setters').once('value'),
    )
    const current = (snap.val() || []) as schema.CloserOrSetter[]
    yield call(() =>
      db
        .ref('Setters')
        .set(current.map((s) => (s.value === old.value ? updated : s))),
    )
    yield put(setterOpResult({ ok: true }))
  } catch (e) {
    log(`${updateSetterFn.name}*() -> ${processErr(e)}`)
    yield put(setterOpResult({ ok: false, err: processErr(e) }))
  }
}

export function* settersSaga() {
  yield all([
    takeEvery(settersAction, settersWatcher),
    takeEvery(addSetter, addSetterFn),
    takeEvery(removeSetter, removeSetterFn),
    takeEvery(updateSetter, updateSetterFn),
  ])
}
